import React, { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Layout,
  Menu,
  Button,
  Avatar,
  Dropdown,
  Typography,
  Space
} from 'antd';
import {
  DashboardOutlined,
  UserOutlined,
  TeamOutlined,
  CalendarOutlined,
  HeartOutlined,
  DollarOutlined,
  HomeOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  SettingOutlined,
  TrophyOutlined,
  DatabaseOutlined
} from '@ant-design/icons';
import { useAuth } from '../../contexts/AuthContext';

const { Header, Sider, Content } = Layout;
const { Text } = Typography;

const AdminLayout = ({ children }) => {
  const [collapsed, setCollapsed] = useState(false);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = () => {
    logout();
    navigate('/login');
  };

  // Base menu items for all admins
  const baseMenuItems = [
    {
      key: '/dashboard',
      icon: <DashboardOutlined />,
      label: <Link to="/dashboard">Dashboard</Link>,
    },
  ];

  // Role-based menu items
  let roleSpecificItems = [];

  if (user?.role === 'super_admin') {
    // Super Admin: Manages Wellness Centers Only
    roleSpecificItems = [
      {
        key: '/centers',
        icon: <HomeOutlined />,
        label: <Link to="/centers">Wellness Centers</Link>,
      },
    ];
  } else {
    // Center Admin: Manages Consumers and Operations
    roleSpecificItems = [
      {
        key: '/consumers',
        icon: <TeamOutlined />,
        label: <Link to="/consumers">Consumers</Link>,
      },
      {
        key: '/attendance',
        icon: <CalendarOutlined />,
        label: <Link to="/attendance">Attendance</Link>,
      },
      {
        key: '/measurements',
        icon: <HeartOutlined />,
        label: <Link to="/measurements">Health Measurements</Link>,
      },
      {
        key: '/payments',
        icon: <DollarOutlined />,
        label: <Link to="/payments">Payments</Link>,
      },
      {
        key: '/coaches',
        icon: <TrophyOutlined />,
        label: <Link to="/coaches">Coaches</Link>,
      },
      {
        key: '/inventory',
        icon: <DatabaseOutlined />,
        label: <Link to="/inventory">Inventory</Link>,
      },
    ];
  }

  const menuItems = [...baseMenuItems, ...roleSpecificItems];

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: 'Profile',
      onClick: () => navigate('/profile'),
    },
    {
      key: 'settings',
      icon: <SettingOutlined />,
      label: 'Settings',
      onClick: () => navigate('/profile'),
    },
    {
      type: 'divider',
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: 'Logout',
      onClick: handleLogout,
    },
  ];

  const selectedKey = location.pathname === '/' ? '/dashboard' : location.pathname;

  return (
    <Layout style={{ minHeight: '100vh' }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        style={{
          background: '#001529',
        }}
      >
        <div className="logo">
          {collapsed ? 'WA' : 'Wellness Admin'}
        </div>
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selectedKey]}
          items={menuItems}
          style={{ borderRight: 0 }}
        />
      </Sider>

      <Layout>
        <Header
          style={{
            padding: '0 16px',
            background: '#fff',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            boxShadow: '0 1px 4px rgba(0,21,41,.08)',
          }}
        >
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
            style={{
              fontSize: '16px',
              width: 64,
              height: 64,
            }}
          />

          <Space align="center">
            <div style={{
              textAlign: 'right',
              marginRight: 16,
              lineHeight: '1.4'
            }}>
              <Text strong style={{
                fontSize: '14px',
                color: '#262626',
                display: 'block'
              }}>
                {user?.name}
              </Text>
              <Text type="secondary" style={{
                fontSize: '12px',
                color: '#8c8c8c'
              }}>
                {user?.role === 'super_admin' ? 'Super Admin' : 'Center Admin'}
                {user?.center_name && ` • ${user.center_name}`}
              </Text>
            </div>
            <Dropdown
              menu={{ items: userMenuItems }}
              placement="bottomRight"
              arrow
            >
              <Avatar
                style={{
                  backgroundColor: '#1890ff',
                  cursor: 'pointer',
                }}
                icon={<UserOutlined />}
              >
                {user?.name?.charAt(0)?.toUpperCase()}
              </Avatar>
            </Dropdown>
          </Space>
        </Header>

        <Content
          style={{
            margin: '16px',
            padding: 24,
            minHeight: 280,
            background: '#f0f2f5',
          }}
        >
          <div style={{
            background: '#fff',
            padding: 24,
            borderRadius: 8,
            minHeight: 'calc(100vh - 112px)',
          }}>
            {children}
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default AdminLayout;
